import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { supabase } from '../lib/supabase';
import { Loader2, Save, Lock, User, FileText, Upload, CheckCircle, X, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../lib/cropUtils';

const Settings = () => {
    const { user, refreshProfile } = useAuth();
    const [loading, setLoading] = useState(false);

    // Profile State
    const [profile, setProfile] = useState({
        name: '',
        designation: '',
        phone: '',
        photo_url: ''
    });

    // Password State
    const [passwordForm, setPasswordForm] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Email Transfer State
    const [emailForm, setEmailForm] = useState({
        newEmail: '',
        password: ''
    });

    const isGoogleUser = user?.app_metadata?.provider === 'google' || user?.app_metadata?.providers?.includes('google');
    const hasPasswordSet = user?.app_metadata?.providers?.includes('email');
    const showOldPasswordField = hasPasswordSet;

    // Cropper State
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Sync local state with global user
    useEffect(() => {
        if (user) {
            setProfile({
                name: user.name || '',
                designation: user.designation || '',
                phone: user.phone || '',
                photo_url: user.avatar_url || ''
            });
        }
    }, [user]);

    const onFileChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result);
                setIsCropperOpen(true);
            });
            reader.readAsDataURL(file);
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropUpload = async () => {
        try {
            setUploading(true);
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const fileName = `${user.id}-${Math.random()}.jpg`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, croppedImageBlob);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(filePath);

            setProfile(prev => ({ ...prev, photo_url: publicUrl }));

            await supabase.from('profiles').update({
                avatar_url: publicUrl,
                updated_at: new Date().toISOString()
            }).eq('id', user.id);

            await refreshProfile();
            toast({ title: "Photo Updated", description: "Your profile photo has been updated." });
            setIsCropperOpen(false);
            setImageSrc(null);
        } catch (error) {
            toast({ title: "Upload Failed", description: error.message, variant: "destructive" });
        } finally {
            setUploading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (profile.phone && !/^\d{10}$/.test(profile.phone)) {
            toast({ title: "Invalid Phone", description: "Phone number must be exactly 10 digits.", variant: "destructive" });
            setLoading(false);
            return;
        }

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    name: profile.name,
                    designation: profile.designation,
                    phone: profile.phone,
                    updated_at: new Date().toISOString()
                })
                .eq('id', user.id);

            if (error) throw error;
            await refreshProfile();
            toast({ title: 'Profile Updated', description: 'Your profile information has been saved.' });
        } catch (error) {
            toast({ title: 'Update Failed', description: error.message, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (showOldPasswordField && !passwordForm.oldPassword) {
            toast({ title: 'Error', description: 'Please enter your old password', variant: 'destructive' });
            return;
        }
        if (passwordForm.newPassword.length < 6) {
            toast({ title: 'Weak Password', description: 'Password must be at least 6 characters.', variant: 'destructive' });
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
            return;
        }

        setLoading(true);
        try {
            if (showOldPasswordField) {
                const { error: signInError } = await supabase.auth.signInWithPassword({
                    email: user.email,
                    password: passwordForm.oldPassword
                });
                if (signInError) throw new Error("Incorrect old password");
            }
            const { error } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
            if (error) throw error;
            toast({ title: 'Password Updated', description: 'Your password has been changed successfully.' });
            setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast({ title: 'Update Failed', description: error.message, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const handleEmailUpdate = async (e) => {
        e.preventDefault();
        if (!emailForm.newEmail || !emailForm.password) {
            toast({ title: 'Missing Fields', description: 'Please enter both new email and your current password.', variant: 'destructive' });
            return;
        }
        setLoading(true);
        try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: emailForm.password
            });
            if (signInError) throw new Error("Authentication failed. Please check your password.");

            const { error } = await supabase.auth.updateUser({ email: emailForm.newEmail });
            if (error) throw error;
            toast({ title: 'Email Update Initiated', description: 'Please check both your old and new email addresses to confirm the change.' });
            setEmailForm({ newEmail: '', password: '' });
        } catch (error) {
            toast({ title: 'Transfer Failed', description: error.message, variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <div className="p-4 md:p-6 max-w-3xl mx-auto min-h-screen bg-[#020617] font-['Inter']">
                <h1 className="text-xl md:text-2xl font-bold text-[#E2E8F0] mb-6 flex items-center gap-2">
                    <User className="w-6 h-6 text-blue-500" />
                    Account Settings
                </h1>

                <div className="space-y-8">
                    {/* Profile Section */}
                    <div className="bg-slate-900/60 backdrop-blur-md p-5 md:p-6 rounded-2xl border border-[rgba(226,232,240,0.1) ] shadow-2xl">
                        <h2 className="text-base font-semibold mb-6 flex items-center gap-2 text-[#E2E8F0]">
                            <User size={18} className="text-blue-400" />
                            Profile Information
                        </h2>
                        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                            <div className="flex flex-col items-center gap-3 group">
                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-slate-950 border-3 border-slate-800 shadow-[0_0_20px_rgba(0,0,0,0.5)] flex items-center justify-center overflow-hidden relative group/avatar">
                                    {profile.photo_url ? (
                                        <img src={profile.photo_url} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={48} className="text-slate-700" />
                                    )}
                                    <label htmlFor="avatar-upload" className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-all duration-300 cursor-pointer backdrop-blur-sm">
                                        <Upload className="text-white transform translate-y-2 group-hover/avatar:translate-y-0 transition-transform duration-300" size={24} />
                                    </label>
                                </div>
                                <div className="text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <p className="text-[10px] text-slate-500 bg-slate-950/80 px-2.5 py-1 rounded-full border border-slate-800 backdrop-blur-md">
                                        Max 2MB
                                    </p>
                                </div>
                                <input id="avatar-upload" type="file" accept="image/*" className="hidden" onChange={onFileChange} />
                            </div>

                            <form onSubmit={handleProfileUpdate} className="space-y-6 flex-1 w-full">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="name" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Full Name</Label>
                                        <Input id="name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="John Doe" className="bg-slate-950 border-slate-800 text-[13px] text-white placeholder:text-slate-800 h-9 focus:border-slate-400 focus:ring-slate-400/10 transition-all" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="designation" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Designation</Label>
                                        <Input id="designation" value={profile.designation} onChange={(e) => setProfile({ ...profile, designation: e.target.value })} placeholder="Financial Analyst" className="bg-slate-950 border-slate-800 text-[13px] text-white placeholder:text-slate-800 h-9 focus:border-slate-400 focus:ring-slate-400/10 transition-all" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="phone" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Phone Number</Label>
                                        <Input id="phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="9876543210" className="bg-slate-950 border-slate-800 text-[13px] text-white placeholder:text-slate-800 h-9 focus:border-slate-400 focus:ring-slate-400/10 transition-all" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={loading} className="bg-slate-200 hover:bg-slate-300 text-slate-950 font-bold px-6 h-9 rounded-lg transition-all shadow-[0_0_10px_rgba(226,232,240,0.1)] text-xs">
                                    {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
                                    Save Profile Changes
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Security Section */}
                    <div className="bg-slate-900/60 backdrop-blur-md p-5 md:p-6 rounded-2xl border border-[rgba(226,232,240,0.1) ] shadow-2xl">
                        <h2 className="text-base font-semibold mb-5 flex items-center gap-2 text-[#E2E8F0]">
                            <Lock size={18} className="text-blue-400" />
                            Security & Credentials
                        </h2>
                        <form onSubmit={handlePasswordUpdate} className="space-y-5">
                            <div className="grid md:grid-cols-3 gap-4">
                                {showOldPasswordField && (
                                    <div className="space-y-1.5">
                                        <Label htmlFor="oldPassword" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Current Password</Label>
                                        <Input id="oldPassword" type="password" value={passwordForm.oldPassword} onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })} placeholder="••••••••" className="bg-slate-950 border-slate-800 text-[13px] text-white h-9 transition-all" />
                                    </div>
                                )}
                                <div className="space-y-1.5">
                                    <Label htmlFor="newPassword" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">New Password</Label>
                                    <Input id="newPassword" type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} placeholder="Min 6" className="bg-slate-950 border-slate-800 text-[13px] text-white h-9 transition-all" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="confirmPassword" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Confirm New</Label>
                                    <Input id="confirmPassword" type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} placeholder="Repeat new" className="bg-slate-950 border-slate-800 text-[13px] text-white h-9 transition-all" />
                                </div>
                            </div>
                            <Button type="submit" disabled={loading} className="bg-slate-200 hover:bg-slate-300 text-slate-950 font-bold px-6 h-9 rounded-lg shadow-[0_0_10px_rgba(226,232,240,0.1)] text-xs">
                                {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Lock className="mr-2 h-3.5 w-3.5" />}
                                Update Password
                            </Button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-slate-800">
                            <h3 className="text-base font-medium mb-3 text-[#E2E8F0] flex items-center gap-2">
                                <Mail size={16} className="text-blue-400" />
                                Email Association Transfer
                            </h3>
                            <p className="text-xs text-slate-500 mb-4 max-w-xl">
                                Transfer your FinAssure account to a different email address. Requires verification from both emails.
                            </p>
                            <form onSubmit={handleEmailUpdate} className="space-y-5">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="newEmail" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">New Email Address</Label>
                                        <Input id="newEmail" type="email" value={emailForm.newEmail} onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })} placeholder="new-email@example.com" className="bg-slate-950 border-slate-800 text-[13px] text-white h-9" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="confirmPassEmail" className="text-[11px] font-medium text-[#94A3B8] ml-1 uppercase tracking-wider">Confirm with Password</Label>
                                        <Input id="confirmPassEmail" type="password" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} placeholder="Your current password" className="bg-slate-950 border-slate-800 text-[13px] text-white h-9" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={loading} className="bg-slate-200 hover:bg-slate-300 text-slate-950 font-bold px-6 h-9 rounded-lg shadow-[0_0_10px_rgba(226,232,240,0.1)] text-xs">
                                    {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="mr-2 h-3.5 w-3.5" />}
                                    Transfer Account Association
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Terms Section */}
                    <div className="bg-slate-900/60 backdrop-blur-md p-5 md:p-6 rounded-2xl border border-[rgba(226,232,240,0.1) ] shadow-2xl">
                        <h2 className="text-base font-semibold mb-4 flex items-center gap-2 text-[#E2E8F0]">
                            <FileText size={18} className="text-blue-400" />
                            Terms & Conditions
                        </h2>
                        <div className="h-36 overflow-y-auto bg-[#0F172A] p-4 rounded-xl border border-slate-800 text-[12px] text-slate-400 leading-relaxed scrollbar-thin scrollbar-thumb-slate-800">
                            <h3 className="text-white font-semibold mb-2">Professional Terms of Service</h3>
                            <p className="mb-3">1. Acceptance: By using FinAssure, you agree to these terms.</p>
                            <p className="mb-3">2. Privacy: Financial data is encrypted and not sold.</p>
                            <p className="mb-3">3. Conduct: No illegal activities or security breaches.</p>
                            <p>4. Liability: FinAssure is an assistant, not professional advice.</p>
                        </div>
                        <div className="mt-4 flex items-center gap-2.5 p-2 bg-emerald-500/5 border border-emerald-500/20 rounded-lg w-fit">
                            <CheckCircle className="text-[#10B981]" size={16} />
                            <label className="text-[11px] font-medium text-slate-500 uppercase tracking-widest">Agreed on Signup (Secure Status)</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cropper Modal */}
            {isCropperOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#020617]/90 backdrop-blur-md p-4">
                    <div className="bg-slate-900 w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col h-[600px]">
                        <div className="flex justify-between items-center p-6 border-b border-white/5 bg-slate-900/50">
                            <h3 className="font-semibold text-xl text-white">Adjust Profile Photo</h3>
                            <button onClick={() => setIsCropperOpen(false)} className="p-2 hover:bg-white/5 rounded-full text-slate-400 hover:text-white transition-colors"><X size={24} /></button>
                        </div>
                        <div className="relative flex-1 bg-black">
                            <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} cropShape="round" showGrid={false} />
                        </div>
                        <div className="p-8 space-y-6 bg-slate-900 border-t border-white/5">
                            <Button onClick={handleCropUpload} disabled={uploading} className="w-full bg-slate-200 hover:bg-slate-300 text-slate-950 font-bold h-12 rounded-xl text-base shadow-xl">
                                {uploading ? <Loader2 className="animate-spin mr-2" /> : <CheckCircle className="mr-2" size={20} />}
                                Finalize & Save Photo
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default Settings;
